import { PageHeader, FilterBar, LoadingState, DataTable, ActionToolbar } from '../components/AdminPageUI';
import SummaryStrip, { SummaryMetric } from '../components/SummaryStrip';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert, Box, Typography, Grid, Button, Card, CardContent, Container,
  Avatar, Stack, Chip, Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, InputAdornment, FormControl, InputLabel,
  Select, MenuItem, Switch, FormControlLabel, TablePagination,
  useMediaQuery, alpha, useTheme
} from '@mui/material';
import { AccountBalanceWallet, Send, Search as SearchIcon, Settings as SettingsIcon, Refresh as RefreshIcon, Close as CloseIcon, PersonAddAltOutlined, EditOutlined, DeleteOutline, GroupsOutlined } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../utils/Api';

const SmsManagementPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Core state
  const [settings, setSettings] = useState({
    smsEnabled: false,
    senderId: '',
    autoTriggers: {
      feesOverdue: false,
      examReports: false,
      announcements: false,
      feePayments: false,
      weeklyDailyFeesSummary: false
    }
  });
  const [balance, setBalance] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [messageTypeFilter, setMessageTypeFilter] = useState('');

  // Bulk SMS state
  const [bulkMessage, setBulkMessage] = useState('');
  const [bulkMessageType, setBulkMessageType] = useState('announcements');
  const [bulkRecipientType, setBulkRecipientType] = useState('all_parents');
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [nonTeachingStaff, setNonTeachingStaff] = useState([]);
  const [selectedNonTeachingStaff, setSelectedNonTeachingStaff] = useState('');
  const [staffForm, setStaffForm] = useState({ name: '', phone: '', position: '' });
  const [editingStaff, setEditingStaff] = useState(null);
  const [staffSaving, setStaffSaving] = useState(false);

  // UI toggles
  const [showSettings, setShowSettings] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [showStaffDialog, setShowStaffDialog] = useState(false);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  // Fetch settings & balance on mount
  useEffect(() => {
    fetchSettings();
    fetchBalance();
    fetchClasses();
    fetchTeachers();
    fetchNonTeachingStaff();
  }, []);

  // Fetch logs when filters/pagination change
  useEffect(() => {
    fetchLogs();
  }, [page, rowsPerPage, debouncedSearch, messageTypeFilter]);

  // Fetch students when class changes
  useEffect(() => {
    if (selectedClass) {
      fetchStudentsInClass(selectedClass);
    } else {
      setStudents([]);
    }
  }, [selectedClass]);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/sms/settings');
      if (res.data.success && res.data.settings) {
        setSettings(res.data.settings);
      }
    } catch (err) {
      toast.error('Failed to load SMS settings');
    }
  };

  const fetchBalance = async () => {
    try {
      const res = await api.get('/sms/balance');
      if (res.data.success) {
        const bal = res.data.balance?.balance || res.data.balance?.data?.balance || '0.00';
        setBalance(bal);
      }
    } catch (err) {
      toast.error('Failed to load SMS balance');
    }
  };

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/sms/logs', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          messageType: messageTypeFilter || undefined,
          search: debouncedSearch || undefined
        }
      });
      if (res.data.success) {
        setLogs(res.data.logs);
        setTotalCount(res.data.totalLogs || res.data.total || 0);
      }
    } catch (err) {
      toast.error('Failed to load SMS logs');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, messageTypeFilter]);

  const fetchClasses = async () => {
    try {
      const res = await api.get('/classes');
      const data = res.data.classes || res.data.data || [];
      setClasses(data);
    } catch (err) {
      toast.error('Failed to load classes');
    }
  };

  const fetchTeachers = async () => {
    try {
      const res = await api.get('/teachers');
      const data = res.data.teachers || res.data.data || [];
      setTeachers(data);
    } catch (err) {
      toast.error('Failed to load teachers');
    }
  };

  const fetchNonTeachingStaff = async () => {
    try {
      const res = await api.get('/sms/non-teaching-staff');
      setNonTeachingStaff(res.data.staff || []);
    } catch (err) {
      toast.error('Failed to load non-teaching staff');
    }
  };

  const fetchStudentsInClass = async (classId) => {
    try {
      const res = await api.get(`/students/class/${classId}`);
      setStudents(res.data.data || res.data || []);
    } catch (err) {
      toast.error('Failed to load students');
    }
  };

  const handleSaveSettings = async () => {
    try {
      const res = await api.put('/sms/settings', settings);
      if (res.data.success) {
        toast.success('SMS settings updated');
        setShowSettings(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update settings');
    }
  };

  const handleSendBulkSms = async () => {
    if (!bulkMessage.trim()) return toast.error('Message cannot be empty');
    if (bulkRecipientType === 'student_parents' && !selectedClass) {
      return toast.error('Please select a class');
    }
    if (bulkRecipientType === 'individual_teacher' && !selectedTeacher) {
      return toast.error('Please select a teacher');
    }
    if (bulkRecipientType === 'individual_non_teaching_staff' && !selectedNonTeachingStaff) {
      return toast.error('Please select a non-teaching staff member');
    }

    try {
      setLoading(true);
      let recipientIds = [];
      if (bulkRecipientType === 'student_parents') {
        recipientIds = students.map(s => s._id);
      } else if (bulkRecipientType === 'individual_teacher') {
        recipientIds = [selectedTeacher];
      } else if (bulkRecipientType === 'individual_non_teaching_staff') {
        recipientIds = [selectedNonTeachingStaff];
      }
      
      const res = await api.post('/sms/bulk', {
        message: bulkMessage,
        messageType: bulkMessageType,
        recipientType: bulkRecipientType,
        recipientIds
      });

      if (res.data.success) {
        const result = res.data.result || {};
        const sentCount = result.recipientsCount ?? 0;
        const skippedCount = result.skippedCount ?? 0;
        const invalidPhoneCount = result.invalidPhoneCount ?? 0;
        const skippedText = skippedCount ? ` (${skippedCount} skipped as duplicate)` : '';
        const invalidText = invalidPhoneCount ? ` ${invalidPhoneCount} invalid number${invalidPhoneCount === 1 ? '' : 's'} skipped.` : '';

        if (sentCount > 0) {
          toast.success(`SMS sent to ${sentCount} recipient${sentCount === 1 ? '' : 's'}${skippedText}.${invalidText}`);
        } else {
          const duplicateText = skippedCount ? ` ${skippedCount} duplicate recipient${skippedCount === 1 ? '' : 's'} skipped.` : '';
          toast.info(result.message
            ? `${result.message}${duplicateText}${invalidText}`
            : `No new SMS sent.${duplicateText}${invalidText || (!duplicateText ? ' Selected recipients may have already received this message recently.' : '')}`
          );
        }

        setBulkMessage('');
        setShowBulkDialog(false);
        fetchLogs();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send SMS');
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerOverdue = async () => {
    try {
      setLoading(true);
      const res = await api.post('/sms/trigger-overdue');
      toast.success(res.data.message || 'Overdue SMS triggered');
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to trigger');
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerWeeklyDailyFees = async () => {
    try {
      setLoading(true);
      const res = await api.post('/sms/trigger-weekly-daily-fees');
      toast.success(res.data.message || 'Weekly daily fees SMS triggered');
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to trigger weekly daily fees SMS');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleString();

  const openStaffDialog = (staff = null) => {
    setEditingStaff(staff);
    setStaffForm({
      name: staff?.name || '',
      phone: staff?.phone || '',
      position: staff?.position || ''
    });
    setShowStaffDialog(true);
  };

  const closeStaffDialog = () => {
    setShowStaffDialog(false);
    setEditingStaff(null);
    setStaffForm({ name: '', phone: '', position: '' });
  };

  const handleSaveStaff = async () => {
    if (!staffForm.name.trim()) return toast.error('Staff name is required');
    if (!staffForm.phone.trim()) return toast.error('Phone number is required');

    try {
      setStaffSaving(true);
      const payload = {
        name: staffForm.name.trim(),
        phone: staffForm.phone.trim(),
        position: staffForm.position.trim()
      };

      if (editingStaff?._id) {
        await api.put(`/sms/non-teaching-staff/${editingStaff._id}`, payload);
        toast.success('Non-teaching staff updated');
      } else {
        await api.post('/sms/non-teaching-staff', payload);
        toast.success('Non-teaching staff added');
      }

      closeStaffDialog();
      fetchNonTeachingStaff();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save non-teaching staff');
    } finally {
      setStaffSaving(false);
    }
  };

  const handleDeleteStaff = async (staff) => {
    if (!window.confirm(`Delete ${staff.name} from non-teaching staff SMS contacts?`)) return;

    try {
      await api.delete(`/sms/non-teaching-staff/${staff._id}`);
      toast.success('Non-teaching staff deleted');
      if (selectedNonTeachingStaff === staff._id) {
        setSelectedNonTeachingStaff('');
      }
      fetchNonTeachingStaff();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete non-teaching staff');
    }
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleBulkRecipientTypeChange = (value) => {
    setBulkRecipientType(value);

    if (value !== 'student_parents') {
      setSelectedClass('');
      setStudents([]);
    }

    if (value !== 'individual_teacher') {
      setSelectedTeacher('');
    }

    if (value !== 'individual_non_teaching_staff') {
      setSelectedNonTeachingStaff('');
    }
  };

  // Reusable mobile log card
  const MobileLogCard = ({ log }) => (
    <Card sx={{ mb: 2, boxShadow: 2 }}>
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {formatDate(log.sentAt)}
            </Typography>
            <Chip
              size="small"
              label={log.status}
              color={
                log.status === 'sent' ? 'success' :
                  log.status === 'failed' ? 'error' : 'warning'
              }
            />
          </Stack>
          <Box>
            <Typography variant="body2" fontWeight="500">{log.recipientPhone}</Typography>
            <Chip size="small" label={log.messageType} sx={{ mt: 0.5 }} />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
            {log.message}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );

  if (loading && logs.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}>
        <LoadingState />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}>
      {/* Header */}
      <PageHeader title={<>SMS Management</>} description={<>Configure settings, send bulk SMS, and view delivery logs</>} actions={<>
        <Button variant="outlined" startIcon={<SettingsIcon />} onClick={() => setShowSettings(true)} fullWidth={isMobile}>
                    Settings
                  </Button>
        <Button variant="contained" startIcon={<Send />} onClick={() => setShowBulkDialog(true)} disabled={!settings.smsEnabled} fullWidth={isMobile}>
                    Send Bulk SMS
                  </Button>
      </>}></PageHeader>
      <SummaryStrip container spacing={isMobile ? 2 : 3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 6, sm: 6, md: 4 }}>
          <SummaryMetric sx={{ boxShadow: isMobile ? 1 : 3, border: `1px solid ${theme.palette.primary.main}`, transition: 'all 0.3s ease', '&:hover': { transform: isMobile ? 'none' : 'translateY(-4px)', boxShadow: isMobile ? 2 : 6 } }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: theme.palette.primary.main, width: isMobile ? 36 : 48, height: isMobile ? 36 : 48 }}>
                  <AccountBalanceWallet fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box>
                  <Typography variant={isMobile ? "caption" : "h6"} color="text.secondary">
                    SMS Balance
                  </Typography>
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
                    {balance !== null ? balance : '—'}
                  </Typography>
                  <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary">
                    {settings.smsEnabled ? 'Service Active' : 'Service Disabled'}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>

        <Grid size={{ xs: 6, sm: 6, md: 4 }}>
          <SummaryMetric sx={{ boxShadow: isMobile ? 1 : 3, border: `1px solid ${theme.palette.info.main}`, transition: 'all 0.3s ease', '&:hover': { transform: isMobile ? 'none' : 'translateY(-4px)', boxShadow: isMobile ? 2 : 6 } }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: theme.palette.info.main, width: isMobile ? 36 : 48, height: isMobile ? 36 : 48 }}>
                  <Send fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box>
                  <Typography variant={isMobile ? "caption" : "h6"} color="text.secondary">
                    Total Sent
                  </Typography>
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
                    {totalCount}
                  </Typography>
                  <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary">
                    All time messages
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>

        <Grid size={{ xs: 6, sm: 6, md: 4 }}>
          <SummaryMetric sx={{ boxShadow: isMobile ? 1 : 3, border: `1px solid ${theme.palette.secondary.main}`, transition: 'all 0.3s ease', '&:hover': { transform: isMobile ? 'none' : 'translateY(-4px)', boxShadow: isMobile ? 2 : 6 } }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: theme.palette.secondary.main, width: isMobile ? 36 : 48, height: isMobile ? 36 : 48 }}>
                  <RefreshIcon fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box>
                  <Typography variant={isMobile ? "caption" : "h6"} color="text.secondary">
                    Auto Triggers
                  </Typography>
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
                    {Object.values(settings.autoTriggers || {}).filter(Boolean).length}/4
                  </Typography>
                  <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary">
                    Active triggers
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>
      </SummaryStrip>
      <FilterBar>
<TextField label="Search message logs"
              size="small"
              placeholder="Search logs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              }}
              sx={{ width: { xs: '100%', sm: 320 } }}
            />
      </FilterBar>

      {/* Stats Cards */}
      

      {/* Non-Teaching Staff Contacts */}
      <Card sx={{ mb: 4, boxShadow: isMobile ? 1 : 3, border: `1px solid ${theme.palette.divider}` }}>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'flex-start' : 'center',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 1.5
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.12), color: theme.palette.info.main }}>
              <GroupsOutlined />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">Non-Teaching Staff SMS Contacts</Typography>
              <Typography variant="body2" color="text.secondary">
                Add staff names and phone numbers so they can receive school SMS notices.
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="contained"
            startIcon={<PersonAddAltOutlined />}
            onClick={() => openStaffDialog()}
            fullWidth={isMobile}
          >
            Add Staff
          </Button>
        </Box>

        {!isMobile ? (
          <Box sx={{ width: '100%', overflow: 'auto' }}>
            <DataTable><Table size="small">
              <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Position</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Phone Number</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {nonTeachingStaff.map((staff) => (
                  <TableRow key={staff._id} sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) } }}>
                    <TableCell>{staff.name}</TableCell>
                    <TableCell>{staff.position || '—'}</TableCell>
                    <TableCell>{staff.phone}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openStaffDialog(staff)}>
                        <EditOutlined fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteStaff(staff)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {nonTeachingStaff.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No non-teaching staff SMS contacts added yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table></DataTable>
          </Box>
        ) : (
          <Stack spacing={1.5} sx={{ p: 2, pt: 0 }}>
            {nonTeachingStaff.map((staff) => (
              <Card key={staff._id} variant="outlined">
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">{staff.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{staff.position || 'Non-teaching staff'}</Typography>
                      <Typography variant="body2">{staff.phone}</Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton size="small" onClick={() => openStaffDialog(staff)}>
                        <EditOutlined fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteStaff(staff)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}
            {nonTeachingStaff.length === 0 && (
              <Typography align="center" color="text.secondary" sx={{ py: 2 }}>
                No non-teaching staff SMS contacts added yet
              </Typography>
            )}
          </Stack>
        )}
      </Card>

      {/* Logs Section */}
      <Card sx={{ boxShadow: isMobile ? 1 : 3, border: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6">SMS Logs</Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            {isMobile && (
              <TextField
                size="small"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                }}
                sx={{ width: 160 }}
              />
            )}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Filter Type</InputLabel>
              <Select
                value={messageTypeFilter}
                label="Filter Type"
                onChange={(e) => setMessageTypeFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="announcements">Announcements</MenuItem>
                <MenuItem value="fees">Fees</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
                <MenuItem value="system">System / Admin</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>

        {!isMobile ? (
          <>
            <Box sx={{ width: '100%', overflow: 'auto', maxHeight: isTablet ? '60vh' : 'none' }}>
              <DataTable><Table>
                <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Recipient</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Message</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow
                      key={log._id}
                      sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) } }}
                    >
                      <TableCell>{formatDate(log.sentAt)}</TableCell>
                      <TableCell>{log.recipientPhone}</TableCell>
                      <TableCell>
                        <Chip size="small" label={log.messageType} />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {log.message}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={log.status}
                          color={
                            log.status === 'sent' ? 'success' :
                              log.status === 'failed' ? 'error' : 'warning'
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {logs.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">No SMS logs found</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table></DataTable>
            </Box>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </>
        ) : (
          <Box sx={{ p: 2 }}>
            {logs.map((log) => (
              <MobileLogCard key={log._id} log={log} />
            ))}
            {logs.length === 0 && !loading && (
              <Typography align="center" color="text.secondary">No SMS logs found</Typography>
            )}
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
            />
          </Box>
        )}
      </Card>

      {/* Settings Dialog */}
      <Dialog
        open={showSettings}
        onClose={() => setShowSettings(false)}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
          <Typography variant="h6" fontWeight="bold">SMS Settings</Typography>
          <IconButton onClick={() => setShowSettings(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Stack spacing={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.smsEnabled}
                  onChange={(e) => setSettings({ ...settings, smsEnabled: e.target.checked })}
                />
              }
              label="Enable SMS Service"
            />
            <TextField
              fullWidth
              label="Sender ID (max 11 characters)"
              value={settings.senderId}
              onChange={(e) => setSettings({ ...settings, senderId: e.target.value.substring(0, 11) })}
            />
            <Typography variant="subtitle1" fontWeight="bold">Auto-Triggers</Typography>
            <Alert severity="info">Enabled alerts also use both guardian contact numbers saved on student records, even without a parent account. Matching parent and guardian numbers receive one SMS per alert.</Alert>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.autoTriggers?.examReports || false}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      autoTriggers: { ...settings.autoTriggers, examReports: e.target.checked }
                    })
                  }
                />
              }
              label="Exam Reports Published"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.autoTriggers?.announcements || false}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      autoTriggers: { ...settings.autoTriggers, announcements: e.target.checked }
                    })
                  }
                />
              }
              label="New Announcements"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.autoTriggers?.feePayments || false}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      autoTriggers: { ...settings.autoTriggers, feePayments: e.target.checked }
                    })
                  }
                />
              }
              label="Fee Payments Recorded"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.autoTriggers?.weeklyDailyFeesSummary || false}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      autoTriggers: { ...settings.autoTriggers, weeklyDailyFeesSummary: e.target.checked }
                    })
                  }
                />
              }
              label="Weekly Summary for Daily School Fees (Fridays)"
            />
            <ActionToolbar direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                color="warning"
                onClick={handleTriggerOverdue}
                disabled={loading || !settings.smsEnabled}
                fullWidth
              >
                Send Overdue Fees SMS
              </Button>
              <Button
                variant="outlined"
                color="info"
                onClick={handleTriggerWeeklyDailyFees}
                disabled={loading || !settings.smsEnabled}
                fullWidth
              >
                Send Weekly Daily Fees SMS
              </Button>
            </ActionToolbar>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: isMobile ? 2 : 3 }}>
          <Button onClick={() => setShowSettings(false)} variant="outlined">Cancel</Button>
          <Button onClick={handleSaveSettings} variant="contained" color="primary">Save Settings</Button>
        </DialogActions>
      </Dialog>

      {/* Non-Teaching Staff Dialog */}
      <Dialog
        open={showStaffDialog}
        onClose={closeStaffDialog}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: alpha(theme.palette.info.main, 0.06) }}>
          <Typography variant="h6" fontWeight="bold">
            {editingStaff ? 'Edit Non-Teaching Staff' : 'Add Non-Teaching Staff'}
          </Typography>
          <IconButton onClick={closeStaffDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Stack spacing={2.5}>
            <TextField
              fullWidth
              label="Staff Name"
              value={staffForm.name}
              onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })}
            />
            <TextField
              fullWidth
              label="Phone Number"
              placeholder="024XXXXXXX"
              value={staffForm.phone}
              onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })}
            />
            <TextField
              fullWidth
              label="Position / Role"
              placeholder="Cleaner, Security, Cook, Driver"
              value={staffForm.position}
              onChange={(e) => setStaffForm({ ...staffForm, position: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: isMobile ? 2 : 3 }}>
          <Button onClick={closeStaffDialog} variant="outlined">Cancel</Button>
          <Button
            onClick={handleSaveStaff}
            variant="contained"
            disabled={staffSaving}
            startIcon={staffSaving ? <CircularProgress size={18} /> : <PersonAddAltOutlined />}
          >
            {editingStaff ? 'Update Staff' : 'Add Staff'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk SMS Dialog */}
      <Dialog
        open={showBulkDialog}
        onClose={() => setShowBulkDialog(false)}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
          <Typography variant="h6" fontWeight="bold">Send Bulk SMS</Typography>
          <IconButton onClick={() => setShowBulkDialog(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Recipient Type</InputLabel>
                  <Select
                    value={bulkRecipientType}
                    label="Recipient Type"
                    onChange={(e) => handleBulkRecipientTypeChange(e.target.value)}
                  >
                    <MenuItem value="all_parents">All Parents</MenuItem>
                    <MenuItem value="student_parents">Parents of Selected Class</MenuItem>
                    <MenuItem value="teachers">All Teachers</MenuItem>
                    <MenuItem value="individual_teacher">Individual Teacher</MenuItem>
                    <MenuItem value="non_teaching_staff">All Non-Teaching Staff</MenuItem>
                    <MenuItem value="individual_non_teaching_staff">Individual Non-Teaching Staff</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Message Type</InputLabel>
                  <Select
                    value={bulkMessageType}
                    label="Message Type"
                    onChange={(e) => setBulkMessageType(e.target.value)}
                  >
                    <MenuItem value="announcements">Announcement</MenuItem>
                    <MenuItem value="fees">Fees</MenuItem>
                    <MenuItem value="reports">Reports</MenuItem>
                    <MenuItem value="attendance">Attendance</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {bulkRecipientType === 'student_parents' && (
              <FormControl fullWidth>
                <InputLabel>Select Class</InputLabel>
                <Select
                  value={selectedClass}
                  label="Select Class"
                  onChange={(e) => setSelectedClass(e.target.value)}
                >
                  {classes.map((c) => (
                    <MenuItem key={c._id} value={c._id}>
                      {c.displayName || c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {bulkRecipientType === 'individual_teacher' && (
              <FormControl fullWidth>
                <InputLabel>Select Teacher</InputLabel>
                <Select
                  value={selectedTeacher}
                  label="Select Teacher"
                  onChange={(e) => setSelectedTeacher(e.target.value)}
                >
                  {teachers.map((t) => (
                    <MenuItem key={t._id} value={t._id}>
                      {t.user?.name || t.name || `${t.firstName} ${t.surname}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {bulkRecipientType === 'individual_non_teaching_staff' && (
              <FormControl fullWidth>
                <InputLabel>Select Non-Teaching Staff</InputLabel>
                <Select
                  value={selectedNonTeachingStaff}
                  label="Select Non-Teaching Staff"
                  onChange={(e) => setSelectedNonTeachingStaff(e.target.value)}
                >
                  {nonTeachingStaff.map((staff) => (
                    <MenuItem key={staff._id} value={staff._id}>
                      {staff.name}{staff.position ? ` - ${staff.position}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {bulkRecipientType === 'all_parents' && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                  bgcolor: alpha(theme.palette.primary.main, 0.06)
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  This SMS will be sent to every parent/guardian phone number in this school, excluding withdrawn student records.
                </Typography>
              </Box>
            )}

            {bulkRecipientType === 'teachers' && (
              <Chip
                label={`This SMS will be sent to all ${teachers.length} teacher${teachers.length === 1 ? '' : 's'}.`}
                color="info"
                variant="outlined"
              />
            )}

            {bulkRecipientType === 'non_teaching_staff' && (
              <Chip
                label={`This SMS will be sent to all ${nonTeachingStaff.length} non-teaching staff contact${nonTeachingStaff.length === 1 ? '' : 's'}.`}
                color="info"
                variant="outlined"
              />
            )}

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Message Body"
              value={bulkMessage}
              onChange={(e) => setBulkMessage(e.target.value)}
            />

            {students.length > 0 && (
              <Chip
                label={`${students.length} student(s) selected`}
                color="primary"
                variant="outlined"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: isMobile ? 2 : 3 }}>
          <Button onClick={() => setShowBulkDialog(false)} variant="outlined">Cancel</Button>
          <Button
            onClick={handleSendBulkSms}
            variant="contained"
            color="primary"
            disabled={loading || !settings.smsEnabled}
            startIcon={loading ? <CircularProgress size={20} /> : <Send />}
          >
            Send SMS
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SmsManagementPage;
